import { useState, useEffect } from 'react'
import { apiErrorMessage } from '../utils/apiError';
import { useNavigate } from 'react-router-dom';
import { 
  UserGroupIcon, 
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FunnelIcon,
  XMarkIcon,
  ArrowsUpDownIcon,
  ChevronUpIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { guestService } from '../services/guestService';
import { patch } from '../utils/httpClient';
import type { Guest, GuestFilters } from '../types/guest';

interface GuestListRow {
  id: string;
  policeFileNumber: string;
  status: 'Ouverte' | 'Validée' | 'Clôturée';
  guestName: string;
  nationality: string;
  roomType: string;
  roomNumber: string;
  checkInDate: string;
  checkOutDate: string;
  amountTTC: number | null;
  currency: string;
  paymentMode: string;
  createdAt: string;
}

const POLICE_FILE_STATUSES = ['Ouverte', 'Validée', 'Clôturée'] as const;
type PoliceFileStatus = (typeof POLICE_FILE_STATUSES)[number];

interface FilterState {
  status: string[];
  dateFrom: string;
  dateTo: string;
  roomType: string[];
  nationality: string[];
}

type SortField = 'checkInDate' | 'checkOutDate' | 'guestName' | 'roomNumber' | 'amountTTC';
type SortOrder = 'asc' | 'desc';

interface SortState {
  field: SortField;
  order: SortOrder;
}

const SORT_FIELD_MAP: Record<SortField, NonNullable<GuestFilters['sortBy']>> = {
  checkInDate: 'check_in_date',
  checkOutDate: 'check_out_date',
  guestName: 'guest_name',
  roomNumber: 'room_number',
  amountTTC: 'total_amount',
};

// Room types from requirements
const ROOM_TYPES = [
  'Standard',
  'Chambre Supérieure',
  'Deluxe',
  'Executive',
  'Suite Junior',
  'Suite Deluxe',
  'Executive Suite'
];

// Common nationalities for filter
const NATIONALITIES = [
  'Congolaise',
  'Française',
  'Belge',
  'Américaine',
  'Britannique',
  'Chinoise',
  'Sud-Africaine',
  'Autre'
];

function mapGuestToListRow(guest: Guest): GuestListRow {
  return {
    id: guest.id,
    policeFileNumber: guest.policeFileNumber || `RDC-${guest.id.substring(0, 8)}`,
    status: guest.policeFileStatus || 'Ouverte',
    guestName: `${guest.firstName} ${guest.lastName}`.trim() || 'N/A',
    nationality: guest.nationality || '-',
    roomType: guest.roomType || '-',
    roomNumber: guest.roomNumber || '-',
    checkInDate: guest.checkInDate || guest.createdAt || '',
    checkOutDate: guest.checkOutDate || '',
    amountTTC: guest.amountTTC ?? null,
    currency: (guest as any).currency || 'USD',
    paymentMode: (guest as any).paymentMode || 'USD',
    createdAt: guest.createdAt || '',
  };
}

export default function GuestListPage() {
  const navigate = useNavigate();
  const [policeFiles, setPoliceFiles] = useState<GuestListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    status: [],
    dateFrom: '',
    dateTo: '',
    roomType: [],
    nationality: []
  });
  const [sort, setSort] = useState<SortState>({
    field: 'checkInDate',
    order: 'desc' // Default: most recent check-in first
  });
  const pageSize = 50;

  // Extend stay
  const [extendingId, setExtendingId] = useState<string | null>(null);
  const [extendDate, setExtendDate] = useState('');
  const [extendSaving, setExtendSaving] = useState(false);

  const handleExtend = async (e: React.MouseEvent, fileId: string) => {
    e.stopPropagation();
    if (extendingId === fileId) {
      setExtendingId(null);
      return;
    }
    setExtendingId(fileId);
    const file = policeFiles.find(f => f.id === fileId);
    if (file?.checkOutDate) {
      const d = new Date(file.checkOutDate);
      d.setDate(d.getDate() + 1);
      setExtendDate(d.toISOString().split('T')[0]);
    } else {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      setExtendDate(d.toISOString().split('T')[0]);
    }
  };

  const submitExtend = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!extendingId || !extendDate) return;
    setExtendSaving(true);
    try {
      const res = await patch(`/api/v1/dashboard/ont/stays/${extendingId}/extend`, {
        new_departure_date: extendDate,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        alert(apiErrorMessage(err, 'Erreur'));
        return;
      }
      setExtendingId(null);
      loadPoliceFiles();
    } catch {
      alert('Erreur de connexion');
    } finally {
      setExtendSaving(false);
    }
  };

  const getDaysUntilCheckout = (checkOutDate: string) => {
    if (!checkOutDate) return null;
    return Math.ceil((new Date(checkOutDate).getTime() - Date.now()) / 86400000);
  };

  useEffect(() => {
    loadPoliceFiles();
  }, [currentPage, searchQuery, filters, sort]);

  const loadPoliceFiles = async () => {
    try {
      setLoading(true);
      const response = await guestService.getGuests(
        {
          search: searchQuery || undefined,
          status: filters.status.length ? filters.status.join(',') : undefined,
          roomType: filters.roomType.length ? filters.roomType.join(',') : undefined,
          nationality: filters.nationality.length ? filters.nationality.join(',') : undefined,
          checkInFrom: filters.dateFrom || undefined,
          checkInTo: filters.dateTo || undefined,
          sortBy: SORT_FIELD_MAP[sort.field],
          sortOrder: sort.order,
        },
        currentPage,
        pageSize
      );
      setPoliceFiles(response.guests.map(mapGuestToListRow));
      setTotalRecords(response.total);
      setTotalPages(Math.max(1, Math.ceil(response.total / response.pageSize)));
    } catch (error) {
      console.error('Error loading police files:', error);
      setPoliceFiles([]);
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setCurrentPage(1);
  };

  const handleFilterChange = (filterType: keyof FilterState, value: any) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleToggleArrayFilter = (filterType: 'status' | 'roomType' | 'nationality', value: string) => {
    setFilters(prev => {
      const currentArray = prev[filterType];
      const newArray = currentArray.includes(value)
        ? currentArray.filter(item => item !== value)
        : [...currentArray, value];
      
      return {
        ...prev,
        [filterType]: newArray
      };
    });
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      status: [],
      dateFrom: '',
      dateTo: '',
      roomType: [],
      nationality: []
    });
    setCurrentPage(1);
  };

  const getActiveFilterCount = (): number => {
    let count = 0;
    if (filters.status.length > 0) count++;
    if (filters.dateFrom || filters.dateTo) count++;
    if (filters.roomType.length > 0) count++;
    if (filters.nationality.length > 0) count++;
    return count;
  };

  const hasActiveFilters = (): boolean => {
    return filters.status.length > 0 ||
           filters.dateFrom !== '' ||
           filters.dateTo !== '' ||
           filters.roomType.length > 0 ||
           filters.nationality.length > 0;
  };

  const getStatusBadgeClass = (status: PoliceFileStatus): string => {
    switch (status) {
      case 'Ouverte':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'Validée':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'Clôturée':
        return 'bg-green-100 text-green-800 border-green-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) {
      return '-';
    }

    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatAmount = (amount: number | null, currencyCode?: string): string => {
    if (amount === null || amount === undefined) {
      return '-';
    }

    const currency = currencyCode || 'USD';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency,
      minimumFractionDigits: currency === 'USD' ? 2 : 0,
    }).format(amount);
  };

  const handleRowClick = (policeFileId: string) => {
    navigate(`/guests/${policeFileId}`);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleSort = (field: SortField) => {
    setSort(prev => {
      // If clicking the same field, toggle order
      if (prev.field === field) {
        return {
          field,
          order: prev.order === 'asc' ? 'desc' : 'asc'
        };
      }
      // If clicking a new field, default to descending for dates/amounts, ascending for text
      return {
        field,
        order: field === 'guestName' || field === 'roomNumber' ? 'asc' : 'desc'
      };
    });
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  const getSortIcon = (field: SortField) => {
    if (sort.field !== field) {
      return <ArrowsUpDownIcon className="h-4 w-4 text-gray-400" />;
    }
    return sort.order === 'asc' 
      ? <ChevronUpIcon className="h-4 w-4 text-purple-600" />
      : <ChevronDownIcon className="h-4 w-4 text-purple-600" />;
  };

  const getSortLabel = (field: SortField): string => {
    const labels: Record<SortField, string> = {
      checkInDate: "Date d'Arrivée",
      checkOutDate: 'Date de Départ',
      guestName: 'Nom',
      roomNumber: 'Chambre',
      amountTTC: 'Montant'
    };
    return labels[field];
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <UserGroupIcon className="h-8 w-8 text-purple-600 mr-3" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Liste des Clients</h1>
                <p className="text-gray-600 mt-1">
                  Rechercher les clients et leurs enregistrements d'identité
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <form onSubmit={handleSearch} className="flex gap-4">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Rechercher par nom, document, chambre, fiche police ou référence..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              />
            </div>
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
              Rechercher
            </button>
            {searchQuery && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                Effacer
              </button>
            )}
          </form>
          {searchQuery && (
            <div className="mt-3 text-sm text-gray-600">
              Résultats pour: <span className="font-medium text-gray-900">"{searchQuery}"</span>
              {totalRecords > 0 && (
                <span className="ml-2">
                  ({totalRecords} {totalRecords === 1 ? 'résultat' : 'résultats'})
                </span>
              )}
            </div>
          )}
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-lg shadow mb-6">
          {/* Filter Toggle Button */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                <FunnelIcon className="h-5 w-5 mr-2 text-gray-400" />
                Filtres
                {getActiveFilterCount() > 0 && (
                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    {getActiveFilterCount()}
                  </span>
                )}
              </button>
              {hasActiveFilters() && (
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
                >
                  <XMarkIcon className="h-4 w-4 mr-1" />
                  Effacer tous les filtres
                </button>
              )}
            </div>
          </div>

          {/* Filter Controls */}
          {showFilters && (
            <div className="px-6 py-4 space-y-6">
              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Statut
                </label>
                <div className="flex flex-wrap gap-2">
                  {POLICE_FILE_STATUSES.map((status) => (
                    <button
                      key={status}
                      type="button"
                      onClick={() => handleToggleArrayFilter('status', status)}
                      className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                        filters.status.includes(status)
                          ? 'bg-purple-100 text-purple-800 border-purple-300'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>

              {/* Date Range Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Période d'arrivée
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="dateFrom" className="block text-xs text-gray-600 mb-1">
                      Du
                    </label>
                    <input
                      type="date"
                      id="dateFrom"
                      value={filters.dateFrom}
                      onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="dateTo" className="block text-xs text-gray-600 mb-1">
                      Au
                    </label>
                    <input
                      type="date"
                      id="dateTo"
                      value={filters.dateTo}
                      onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Room Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type de chambre
                </label>
                <div className="flex flex-wrap gap-2">
                  {ROOM_TYPES.map((roomType) => (
                    <button
                      key={roomType}
                      type="button"
                      onClick={() => handleToggleArrayFilter('roomType', roomType)}
                      className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                        filters.roomType.includes(roomType)
                          ? 'bg-purple-100 text-purple-800 border-purple-300'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {roomType}
                    </button>
                  ))}
                </div>
              </div>

              {/* Nationality Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nationalité
                </label>
                <div className="flex flex-wrap gap-2">
                  {NATIONALITIES.map((nationality) => (
                    <button
                      key={nationality}
                      type="button"
                      onClick={() => handleToggleArrayFilter('nationality', nationality)}
                      className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                        filters.nationality.includes(nationality)
                          ? 'bg-purple-100 text-purple-800 border-purple-300'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {nationality}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Active Filters Display */}
          {hasActiveFilters() && (
            <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Filtres actifs:</span>
                
                {filters.status.map((status) => (
                  <span
                    key={`active-status-${status}`}
                    className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-800"
                  >
                    Statut: {status}
                    <button
                      type="button"
                      onClick={() => handleToggleArrayFilter('status', status)}
                      className="ml-1.5 inline-flex items-center justify-center hover:text-purple-900"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </span>
                ))}

                {(filters.dateFrom || filters.dateTo) && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-800">
                    Période: {filters.dateFrom || '...'} - {filters.dateTo || '...'}
                    <button
                      type="button"
                      onClick={() => {
                        handleFilterChange('dateFrom', '');
                        handleFilterChange('dateTo', '');
                      }}
                      className="ml-1.5 inline-flex items-center justify-center hover:text-purple-900"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </span>
                )}

                {filters.roomType.map((roomType) => (
                  <span
                    key={`active-room-${roomType}`}
                    className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-800"
                  >
                    Chambre: {roomType}
                    <button
                      type="button"
                      onClick={() => handleToggleArrayFilter('roomType', roomType)}
                      className="ml-1.5 inline-flex items-center justify-center hover:text-purple-900"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </span>
                ))}

                {filters.nationality.map((nationality) => (
                  <span
                    key={`active-nationality-${nationality}`}
                    className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-800"
                  >
                    Nationalité: {nationality}
                    <button
                      type="button"
                      onClick={() => handleToggleArrayFilter('nationality', nationality)}
                      className="ml-1.5 inline-flex items-center justify-center hover:text-purple-900"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sort Controls */}
        <div className="bg-white rounded-lg shadow mb-6 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <ArrowsUpDownIcon className="h-5 w-5 text-gray-400 mr-2" />
              <span className="text-sm font-medium text-gray-700">Trier par:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {(['checkInDate', 'checkOutDate', 'guestName', 'roomNumber', 'amountTTC'] as SortField[]).map((field) => (
                <button
                  key={field}
                  type="button"
                  onClick={() => handleSort(field)}
                  className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                    sort.field === field
                      ? 'bg-purple-100 text-purple-800 border-purple-300'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {getSortLabel(field)}
                  <span className="ml-1.5">
                    {getSortIcon(field)}
                  </span>
                </button>
              ))}
            </div>
          </div>
          {sort.field && (
            <div className="mt-2 text-xs text-gray-600">
              Tri actuel: <span className="font-medium">{getSortLabel(sort.field)}</span> 
              {' '}({sort.order === 'asc' ? 'croissant' : 'décroissant'})
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-lg shadow">
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('guestName')}
                  >
                    <div className="flex items-center">
                      Nom du Client
                      <span className="ml-1">
                        {getSortIcon('guestName')}
                      </span>
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('roomNumber')}
                  >
                    <div className="flex items-center">
                      Numéro de Chambre
                      <span className="ml-1">
                        {getSortIcon('roomNumber')}
                      </span>
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('checkInDate')}
                  >
                    <div className="flex items-center">
                      Date d'Arrivée
                      <span className="ml-1">
                        {getSortIcon('checkInDate')}
                      </span>
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Départ
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Statut
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('amountTTC')}
                  >
                    <div className="flex items-center">
                      Montant
                      <span className="ml-1">
                        {getSortIcon('amountTTC')}
                      </span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <div className="flex justify-center items-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                        <span className="ml-3 text-gray-600">Chargement...</span>
                      </div>
                    </td>
                  </tr>
                ) : policeFiles.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600">Aucun client trouvé</p>
                    </td>
                  </tr>
                ) : (
                  policeFiles.map((file) => (
                    <tr
                      key={file.id}
                      onClick={() => handleRowClick(file.id)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {file.guestName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {file.policeFileNumber}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{file.roomNumber}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(file.checkInDate)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {file.checkOutDate ? (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-900">{formatDate(file.checkOutDate)}</span>
                            {file.status === 'Ouverte' && (() => {
                              const days = getDaysUntilCheckout(file.checkOutDate);
                              if (days !== null && days <= 1) return <span className="px-1.5 py-0.5 text-xs rounded bg-red-100 text-red-700">Imminent</span>;
                              if (days !== null && days <= 3) return <span className="px-1.5 py-0.5 text-xs rounded bg-orange-100 text-orange-700">{days}j</span>;
                              return null;
                            })()}
                            {file.status !== 'Clôturée' && (
                              <button
                                onClick={(e) => handleExtend(e, file.id)}
                                className="px-2 py-0.5 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100"
                              >
                                Prolonger
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                        {extendingId === file.id && (
                          <div className="mt-2 flex items-center gap-2" onClick={e => e.stopPropagation()}>
                            <input
                              type="date"
                              value={extendDate}
                              onChange={e => setExtendDate(e.target.value)}
                              className="px-2 py-1 text-sm border border-blue-300 rounded focus:ring-1 focus:ring-blue-500"
                            />
                            <button onClick={submitExtend} disabled={extendSaving} className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                              {extendSaving ? '...' : 'OK'}
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); setExtendingId(null); }} className="text-xs text-gray-500 hover:text-gray-700">Annuler</button>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadgeClass(
                            file.status
                          )}`}
                        >
                          {file.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatAmount(file.amountTTC, file.currency)}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && policeFiles.length > 0 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Précédent
                </button>
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Suivant
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Affichage de{' '}
                    <span className="font-medium">
                      {(currentPage - 1) * pageSize + 1}
                    </span>{' '}
                    à{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * pageSize, totalRecords)}
                    </span>{' '}
                    sur <span className="font-medium">{totalRecords}</span> résultats
                  </p>
                </div>
                <div>
                  <nav
                    className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                    aria-label="Pagination"
                  >
                    <button
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Précédent</span>
                      <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                    <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                      Page {currentPage} sur {totalPages}
                    </span>
                    <button
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Suivant</span>
                      <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
